<script lang="ts" setup>
import { storeToRefs } from 'pinia'

import { useBasketMethods } from '@/composables/useBasketMethods'

import { useAppStore } from '@/store/app/app'
import { useCityStore } from '@/store/city/city'
import { useToggleStore } from '@/store/toggle/toggle'

import TheCookiesBanner from '@/components/App/AppCookiesBanner.vue'
import AppSnackbar from '@/components/App/AppSnackbar.vue'
import TheFooter from '@/components/Footer/TheFooter.vue'
import MainCategoryList from '@/components/Main/MainCategoryList.vue'

const TheHeaderMobile = defineAsyncComponent(
  () => import('@/components/Header/TheHeaderMobile.vue'),
)

const MainDialogWorking = defineAsyncComponent(
  () => import('@/components/Main/MainDialogWorking.vue'),
)

const DrawerContent = defineAsyncComponent(
  () => import('@/components/Header/HeaderNavigationDrawer/DrawerContent.vue'),
)
const TheApplicationBanner = defineAsyncComponent(
  () => import('@/components/App/TheApplicationBanner.vue'),
)
const HeaderBasketMobileWidget = defineAsyncComponent(
  () => import('@/components/Header/HeaderBasketMobileWidget.vue'),
)

const { fetchBasket, handleRedirectCity } = useBasketMethods()

const PROFILE_DISABLED_BASKET_ROUTES = [
  'profile-settings',
  'profile-notifications',
  'profile-password-change',
]

const route = useRoute()

const cityStore = useCityStore()
const { isVisibleStories } = storeToRefs(useToggleStore())
const { isWebview } = useAppStore()

const showNavigationDrawer = ref(false)

const isActive = computed(() => cityStore.cityInfo.is_active)

const isHomePage = computed(() => route.name === 'index')

const isBasketPage = computed(() => route.name === 'basket')

const showBasketWidget = computed(() => {
  if (PROFILE_DISABLED_BASKET_ROUTES.includes(String(route.name))) {
    return false
  }

  return isActive.value && !isBasketPage.value && !isWebview
})

const isShowCategoryList = computed(() => {
  return (
    String(route.name).includes('category') ||
    String(route.name).includes('delivery-slug')
  )
})

onMounted(() => {
  fetchBasket()

  handleRedirectCity()
})
</script>

<template>
  <div
    class="mobile"
    :class="{ 'mobile--scroll-locked': showNavigationDrawer }"
  >
    <the-application-banner v-if="!isWebview && !showNavigationDrawer" />

    <the-header-mobile
      v-if="!isWebview"
      class="mobile__header"
      :class="{ 'mobile__header--fixed': showNavigationDrawer }"
      :is-active-navigation="showNavigationDrawer"
      @on-open-navigation="showNavigationDrawer = !showNavigationDrawer"
    />

    <the-navigation-drawer
      v-if="!isWebview"
      v-model="showNavigationDrawer"
      top="60"
      overlay
    >
      <drawer-content />
    </the-navigation-drawer>

    <div class="mobile__wrapper">
      <app-stories
        v-if="isVisibleStories && isHomePage"
        class="mobile__stories page--container"
      />

      <app-snackbar />

      <main-category-list v-if="isShowCategoryList" is-category-page />

      <div class="page">
        <slot />
      </div>

      <main-dialog-working />
    </div>

    <header-basket-mobile-widget v-show="showBasketWidget" />

    <the-footer v-if="!isWebview" />

    <client-only>
      <the-cookies-banner v-if="!isWebview" />
    </client-only>
  </div>
</template>

<style lang="scss" scoped>
.mobile {
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;
  background-color: var(--ui-white);

  &--scroll-locked {
    overflow: hidden;
  }

  &__header {
    z-index: 1005;

    &--fixed {
      position: fixed !important;
      height: 60px;
    }
  }

  &__stories {
    width: 100%;
    padding-right: 0;
  }

  &__wrapper {
    position: relative;
  }

  &__banner {
    @include media('>=phone', '<=desktop-mini') {
      padding: 20px 20px 0 20px;
    }
  }
}

.page {
  flex-grow: 1;
  min-height: calc(100vh - 104px);
}
</style>
